import { observable, action } from 'mobx';
import _ from 'lodash';

import { Bloodwork, BloodworkRange } from '../models';
import { BloodworkStore } from './bloodwork-store';
import { LoadingStore } from './loading-store';
import { UserProfileUIStore } from './user-profile-ui-store';

export class BloodworkEntryStore {
  static DEFAULT_LIMIT: number = 5;
  
  @observable bloodworks: Bloodwork[] = [];
  @observable clientBloodworks: Bloodwork[] = [];
  @observable bloodworkRanges: BloodworkRange[] = [];
  @observable currentBloodwork: Bloodwork = new Bloodwork();
  @observable hideEnterTestResults: boolean = false;

  constructor(
    private bloodworkStore: BloodworkStore, 
    private loadingStore: LoadingStore,
    private userProfileUIStore: UserProfileUIStore) {
  }

  @action refreshBloodworks = (): Promise<any> => {
    console.log('***************** refreshBloodworks **********************************');
    this.bloodworks = [];
    return this.loadBloodworks(BloodworkEntryStore.DEFAULT_LIMIT);
  }

  @action loadClientBloodworks = async (clientId: string): Promise<Bloodwork[]> => {
    this.loadingStore.appIsBusy();
    
    const clientBloodworks = await this.bloodworkStore.load(clientId);
    this.clientBloodworks = _.orderBy(clientBloodworks, ['collectionDate', 'createDate'], ['desc', 'desc']);
    
    this.loadingStore.appIsNotBusy();
    
    return this.clientBloodworks;
  }

  @action resetClientBloodworks(): void {
    this.clientBloodworks = [];
  }

  @action setHideEnterTestResults(hideEnterTestResults: boolean): void {
    this.hideEnterTestResults = hideEnterTestResults;
  }

  @action loadBloodworks = (limit: number, lastCreateDate?: string): Promise<any> => {
    console.log('***************** loadBloodworks **********************************');
    this.loadingStore.appIsBusy();
    return this.bloodworkStore.load(undefined, limit, lastCreateDate).then((data) => {
      this.bloodworks.push(...data.bloodworks);
      this.loadingStore.appIsNotBusy();
      return data;
    }, (error: any) => {
      console.error(error);
      this.loadingStore.appIsNotBusy();
      return {};
    });
  }

  @action setCurrentBloodwork = (bloodwork: Bloodwork): void => {
    this.currentBloodwork = bloodwork;
  }

  @action resetCurrentBloodwork = (): Bloodwork => {
    this.currentBloodwork = new Bloodwork();
    return this.currentBloodwork;
  }

  // Mirrors FurtherConsiderationsEntryStore.loadFurtherConsiderations: the
  // BloodworkRange table is identical for every practitioner, read-only from
  // the app, and ~650KB of JSON, but four containers refetched it on every
  // mount. One shared promise per session; failures are not cached so the next
  // mount still retries.
  private rangesPromise?: Promise<BloodworkRange[]>;

  loadBloodworkRanges(): Promise<BloodworkRange[]> {
    if (this.rangesPromise) {
      return this.rangesPromise;
    }

    this.loadingStore.appIsBusy();
    this.rangesPromise = this.bloodworkStore.loadBloodworkRanges().then((bloodworkRanges) => {
      this.bloodworkRanges = bloodworkRanges;
      this.loadingStore.appIsNotBusy();
      return bloodworkRanges;
    }, (error: any) => {
      console.error(error);
      this.rangesPromise = undefined;
      this.loadingStore.appIsNotBusy();
      return [];
    });
    return this.rangesPromise;
  }

  /**
   * Put a freshly saved test back over the copy these lists are holding.
   *
   * A test can exist twice in memory: the row in `bloodworks` /
   * `clientBloodworks`, and the copy BloodworkAnalysis makes of it
   * (`new Bloodwork(...)` in its constructor). Everything saved from the
   * analysis screen — section notes, supplement panel overrides, the client
   * list — is written to that copy, and nothing used to bring the result back
   * here. Manage Test Results' Edit button hands the entry form the row from
   * these lists, and the entry form re-posts whatever it was given.
   *
   * Notes happen to survive that: BloodworkNotes' constructor assigns the same
   * `notes` array rather than copying it, so the copy shares the note graph and
   * every in-place note write lands on the row too. Nothing else does. A field
   * the analysis screen ASSIGNS (`bloodwork.supplementEdits = [...]`) never
   * reaches the row at all, and the first such edit on a test is lost the next
   * time that test is edited — there is no shared array to mutate.
   *
   * So rather than leaning on that accident, the save closes the loop: one
   * test, one current copy. The record written back is the one the API echoed,
   * which is the item it stored.
   */
  @action private refreshSavedBloodwork = (saved: Bloodwork): void => {
    if (!saved || !saved.id) {
      return;
    }
    const replaceIn = (list: Bloodwork[]) => {
      const index = (list || []).findIndex(
        bloodwork => bloodwork && bloodwork.id === saved.id,
      );
      if (index !== -1) {
        list[index] = saved;
      }
    };
    replaceIn(this.bloodworks);
    replaceIn(this.clientBloodworks);
    // `currentBloodwork` is deliberately left alone. It is the entry form's own
    // record, set from these lists when Edit is clicked, and swapping it
    // underneath an open form would re-seed every field the practitioner is
    // part-way through typing.
  }

  /**
   * `silent` keeps the global spinner modal down for a background autosave.
   *
   * That spinner is a Bootstrap Modal, and while it is up the body carries
   * `modal-open` (overflow: hidden), which collapses the scrollable height and
   * pins the page at the top — the position is NOT restored when the modal
   * closes. For a save the practitioner asked for that costs a scroll the
   * caller can put back (see scrollPanelIntoView), but a save that fires by
   * itself while they are working must not move the page at all: the
   * Recommended Supplements card autosaves 800ms after a tick or a picked
   * product, and the practitioner is still reading the row they just clicked.
   *
   * A silent save touches neither appIsBusy nor appIsNotBusy: clearing the flag
   * would hide a spinner a concurrent user-initiated save had legitimately
   * raised. Silent saves report their own state instead — the card carries a
   * Saving / Changes saved line beside the button that caused them.
   */
  saveBloodwork(
    bloodwork: Bloodwork,
    options: { silent?: boolean } = {},
  ): Promise<Bloodwork> {
    const silent = !!options.silent;
    if (!silent) this.loadingStore.appIsBusy();
    const wasNew = bloodwork.isNew();
    return this.bloodworkStore.saveBloodwork(bloodwork).then((savedBloodwork) => {
      if (wasNew) {
        this.userProfileUIStore.currentUserProfile.incrementNumTests();
        this.userProfileUIStore.saveCurrentUserProfile();
      }
      this.refreshSavedBloodwork(savedBloodwork);
      if (!silent) this.loadingStore.appIsNotBusy();
      return savedBloodwork;
    }, (error: any) => {
      console.error(error);
      if (!silent) this.loadingStore.appIsNotBusy();
      throw error;
    });
  }

  @action deleteBloodwork(bloodwork: Bloodwork): Promise<any> {
    this.loadingStore.appIsBusy();
    return this.bloodworkStore.deleteBloodwork(bloodwork).then((result) => {
      return this.refreshBloodworks();
    }).then(() => {
      this.loadingStore.appIsNotBusy();
    }).catch((error) => {
      console.error(error);
      this.loadingStore.appIsNotBusy();
    });
  }

  @action deleteClientBloodworks(clientId: string): Promise<any> {
    if (this.clientBloodworks.length === 0) {
      return Promise.resolve();
    }

    this.loadingStore.appIsBusy();
    const promises = [];
    this.clientBloodworks.forEach((bloodwork) => {
      promises.push(this.bloodworkStore.deleteBloodwork(bloodwork));
    });
    return Promise.all(promises).then(() => {
      return this.loadClientBloodworks(clientId);
    }, (error) => {
      console.error(error);
      return this.loadClientBloodworks(clientId);
    }).catch((error) => {
      console.error(error);
      return this.loadClientBloodworks(clientId);
    });
  }
}